<template>
  <div class="stepper-content">
    <v-row class="step2-row ma-0" no-gutters>
      <v-col cols="12" md="6" class="pa-3">
        <v-card class="step-card rounded-lg" outlined>
          <v-card-title class="section-title step2-left-header">
            <span>Generate edits</span>
            <div class="step2-header-chips d-flex align-center">
              <v-menu
                offset-y
                left
                :close-on-content-click="false"
                content-class="step2-prompt-popover"
              >
                <template #activator="{ on, attrs }">
                  <v-chip
                    small
                    color="primary"
                    outlined
                    class="step2-header-chip step2-prompt-mode-chip step2-type-chip"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left small class="step2-prompt-mode-chip__icon">mdi-text-box-outline</v-icon>
                    <span class="step2-header-chip__label step2-prompt-mode-chip__label">{{ promptModeLabel }}</span>
                    <v-icon right small class="step2-type-chevron">mdi-chevron-down</v-icon>
                  </v-chip>
                </template>
                <div class="step2-prompt-popover__inner">
                  <p class="step2-prompt-popover__hint text-caption text--secondary mb-2">
                    Edit the AI prompt here, then run Generate edits again.
                  </p>
                  <v-textarea
                    outlined
                    dense
                    hide-details
                    rows="6"
                    class="step2-prompt-popover__input"
                    :value="promptEditorDisplay"
                    @input="onPromptEditorInput"
                  />
                </div>
              </v-menu>
              <v-menu
                v-if="models.length"
                offset-y
                left
                content-class="step2-type-dropdown"
                :close-on-content-click="true"
              >
                <template #activator="{ on, attrs }">
                  <v-chip
                    small
                    color="primary"
                    outlined
                    class="step2-type-chip step2-header-chip"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left small>mdi-robot-outline</v-icon>
                    <span class="step2-header-chip__label">{{ currentModelLabel }}</span>
                    <v-icon right small class="step2-type-chevron">mdi-chevron-down</v-icon>
                  </v-chip>
                </template>
                <v-list dense class="step2-type-list">
                  <v-list-item
                    v-for="m in models"
                    :key="m.value"
                    :class="{ 'v-list-item--active': modelValue === m.value }"
                    class="step2-type-list-item"
                    @click="selectModel(m)"
                  >
                    <v-list-item-title class="step2-type-list-title">{{ m.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-menu
                v-if="printTypes.length"
                offset-y
                left
                content-class="step2-type-dropdown"
                :close-on-content-click="true"
              >
                <template #activator="{ on, attrs }">
                  <v-chip
                    small
                    color="primary"
                    outlined
                    class="step2-type-chip step2-header-chip"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left small>mdi-tag-outline</v-icon>
                    <span class="step2-header-chip__label">{{ currentTypeLabel }}</span>
                    <v-icon right small class="step2-type-chevron">mdi-chevron-down</v-icon>
                  </v-chip>
                </template>
                <v-list dense class="step2-type-list">
                  <v-list-item
                    v-for="type in printTypes"
                    :key="type.value"
                    :class="{ 'v-list-item--active': printTypeValue === type.value }"
                    class="step2-type-list-item"
                    @click="selectType(type)"
                  >
                    <v-list-item-title class="step2-type-list-title">{{ type.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-card-title>
          <v-divider class="section-divider" />
          <v-card-text class="section-body">
            <p class="text-body-2 mb-3">
              Select an input image below, then click Generate edits to run the AI.
            </p>
            <div class="step2-input-gallery-wrap">
              <ImageGallery
                :value="selectedIds"
                :items="inputGalleryItems"
                :allow-multiple="false"
                :show-upload-button="false"
                image-key="imageUrl"
                class="step2-input-gallery"
                @input="$emit('update:selectedIds', $event)"
                @view="$emit('view', $event)"
                @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
              />
            </div>
            <template v-if="loading">
              <div class="step2-loading-left mt-3">
                <h3 class="step2-loading-left__title">Generating your edits</h3>
                <p class="step2-loading-left__subtitle">AI is creating variations of your image. This usually takes a few seconds.</p>
                <div class="step2-loading-left__bar-wrap">
                  <div class="step2-loading-left__bar" />
                </div>
              </div>
            </template>
            <template v-else>
              <v-btn
                color="primary"
                class="mt-3"
                :disabled="!selectedItems.length"
                @click="$emit('generate')"
              >
                Generate edits
              </v-btn>
              <div v-if="responseTime" class="mt-3 text-caption text--secondary">
                Last run: {{ responseTime }}
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" class="pa-3">
        <v-card class="step-card rounded-lg" outlined>
          <v-card-title class="section-title">Generated edits</v-card-title>
          <v-divider class="section-divider" />
          <v-card-text class="section-body step2-gallery-wrap">
            <template v-if="loading">
              <div class="step2-section">
                <h4 class="step2-section-heading">Newly generated</h4>
                <div class="step2-loading-skeletons">
                  <div v-for="i in 3" :key="i" class="step2-loading-skeletons__item" />
                </div>
              </div>
              <div v-if="previouslyGeneratedEditsItems.length" class="step2-section">
                <h4 class="step2-section-heading">Previously generated</h4>
                <ImageGallery
                  :value="selectedEditId"
                  :items="previouslyGeneratedEditsItems"
                  :allow-multiple="false"
                  :show-upload-button="false"
                  :selectable="false"
                  image-key="imageUrl"
                  @input="$emit('update:selectedEditId', $event)"
                  @view="$emit('view', $event)"
                  @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
                />
              </div>
            </template>
            <template v-else>
              <div v-if="editsGalleryItems.length" class="step2-section">
                <h4 class="step2-section-heading">Newly generated</h4>
                <ImageGallery
                  :value="selectedEditId"
                  :items="editsGalleryItems"
                  :allow-multiple="false"
                  :show-upload-button="false"
                  :selectable="false"
                  image-key="imageUrl"
                  @input="$emit('update:selectedEditId', $event)"
                  @view="$emit('view', $event)"
                  @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
                />
              </div>
              <div v-if="previouslyGeneratedEditsItems.length" class="step2-section">
                <h4 class="step2-section-heading">Previously generated</h4>
                <ImageGallery
                  :value="selectedEditId"
                  :items="previouslyGeneratedEditsItems"
                  :allow-multiple="false"
                  :show-upload-button="false"
                  :selectable="false"
                  image-key="imageUrl"
                  @input="$emit('update:selectedEditId', $event)"
                  @view="$emit('view', $event)"
                  @nano-banana-deleted="$emit('nano-banana-deleted', $event)"
                />
              </div>
              <p v-if="!editsGalleryItems.length && !previouslyGeneratedEditsItems.length" class="text-body-2 text--secondary">
                No edits yet. Select an image on the left and click Generate edits.
              </p>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import prompts from '@/assets/mizodia/custom-print-prompts.json'
import { ImageGallery } from '@/components/ImageGallery'
import { defaultModelValueForPrintType } from './constants'

export default {
  name: 'Step2Content',
  components: { ImageGallery },
  props: {
    loading: { type: Boolean, default: false },
    printTypeLabel: { type: String, default: '' },
    printType: { type: String, default: 'fingerprint' },
    printTypes: { type: Array, default: () => [] },
    model: { type: String, default: 'gemini-2.5-flash-image' },
    models: { type: Array, default: () => [] },
    /** 0 = predefined (default prompt), 1 = custom prompt — matches TypeSelectionCard tabs. */
    selectedTab: { type: Number, default: 0 },
    prompt: { type: String, default: '' },
    /** Current predefined prompt for the selected print type (when tab is Default). */
    predefinedPromptText: { type: String, default: '' },
    edits: { type: Array, default: () => [] },
    inputGalleryItems: { type: Array, default: () => [] },
    selectedIds: { type: [String, Number, Array], default: null },
    selectedItems: { type: Array, default: () => [] },
    responseTime: { type: String, default: null },
    selectedEditId: { type: [String, Number], default: null },
    editsGalleryItems: { type: Array, default: () => [] },
    previouslyGeneratedEditsItems: { type: Array, default: () => [] },
  },
  computed: {
    printTypeValue() {
      return this.printType || 'fingerprint'
    },
    currentTypeLabel() {
      const found = this.printTypes.find((t) => t.value === this.printTypeValue)
      return found ? found.label : this.printTypeLabel || 'Fingerprint'
    },
    modelValue() {
      return this.model || 'gemini-2.5-flash-image'
    },
    currentModelLabel() {
      const found = this.models.find((m) => m.value === this.modelValue)
      return found ? found.label : 'Model'
    },
    promptModeLabel() {
      return this.selectedTab === 1 ? 'Custom' : 'Default'
    },
    promptEditorDisplay() {
      return this.selectedTab === 1 ? (this.prompt || '') : (this.predefinedPromptText || '')
    },
  },
  methods: {
    selectType(type) {
      if (!type || !type.value) return
      const key = type.value
      this.$emit('update:printType', key)
      this.$emit('update:selectedTab', 0)
      this.$emit('update:prompt', prompts[key] || '')
      this.$emit('update:model', defaultModelValueForPrintType(key))
    },
    selectModel(m) {
      if (m && m.value) this.$emit('update:model', m.value)
    },
    onPromptEditorInput(val) {
      const text = typeof val === 'string' ? val : (val && val.target != null ? val.target.value : '') || ''
      this.$emit('update:prompt', text)
      if (this.selectedTab === 0) this.$emit('update:selectedTab', 1)
    },
  },
}
</script>

<style scoped>
.step2-row {
  min-height: 0;
}

.step2-left-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100%;
}

.step2-header-chips {
  flex-shrink: 0;
  margin-left: 12px;
  gap: 8px;
  max-width: min(100%, 520px);
  justify-content: flex-end;
}

.step2-header-chip {
  flex-shrink: 0;
  max-width: 200px;
}

.step2-header-chip__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step2-type-chip {
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
}

.step2-type-chip:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.step2-type-chevron {
  margin-left: 2px;
  opacity: 0.9;
}

.step2-prompt-mode-chip {
  max-width: none;
}

.step2-prompt-mode-chip__label {
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.6875rem;
}

.step2-input-gallery-wrap {
  min-height: 200px;
}

.step2-input-gallery {
  min-height: 180px;
}

.step2-gallery-wrap {
  min-height: 280px;
}

.step2-section {
  margin-bottom: 24px;
}

.step2-section:last-child {
  margin-bottom: 0;
}

.step2-section-heading {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 12px;
  padding: 0;
}

.step-card {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.section-title {
  font-size: 0.9375rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  padding: 18px 20px 14px !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.section-divider {
  margin: 0 20px !important;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

/* Dropdown menu – compact, chip-like */
.step2-type-list {
  min-width: 140px;
  padding: 4px 0;
}

.step2-type-list .v-list-item {
  min-height: 0;
  padding: 6px 14px !important;
}

.step2-type-list-item .v-list-item__content {
  padding: 0;
}

.step2-type-list-title {
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.section-body {
  text-align: left;
  padding: 18px 20px 20px !important;
  padding-top: 16px !important;
}

.step2-loading-left {
  text-align: left;
}

.step2-loading-left__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 8px;
}

.step2-loading-left__subtitle {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 20px;
}

.step2-loading-left__bar-wrap {
  height: 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
  max-width: 280px;
}

.step2-loading-left__bar {
  height: 100%;
  width: 40%;
  border-radius: 6px;
  background: linear-gradient(90deg, #42a5f5, #64b5f6);
  animation: step2-bar 1.5s ease-in-out infinite;
}

@keyframes step2-bar {
  0% { transform: translateX(-100%); }
  50% { transform: translateX(250%); }
  100% { transform: translateX(-100%); }
}

.step2-loading-skeletons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.step2-loading-skeletons__item {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: step2-skeleton 1.8s ease-in-out infinite;
}

@keyframes step2-skeleton {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>

<style lang="scss">
/* Menu dropdown (portaled to body) – compact, chip-like, no icons */
.step2-type-dropdown.v-menu__content {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  padding: 4px 0 !important;
}

.step2-type-dropdown .step2-type-list {
  min-width: 140px;
  padding: 4px 0;
}

.step2-type-dropdown .step2-type-list .v-list-item {
  min-height: 0;
  padding: 6px 14px !important;
}

.step2-type-dropdown .step2-type-list-item .v-list-item__content {
  padding: 0;
}

.step2-type-dropdown .step2-type-list-title {
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

/* Prompt quick-edit (portaled) */
.step2-prompt-popover.v-menu__content {
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
  min-width: min(92vw, 320px);
  max-width: min(92vw, 440px);
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0;
}

.step2-prompt-popover .step2-prompt-popover__inner {
  padding: 12px 14px 14px;
}

.step2-prompt-popover .step2-prompt-popover__hint {
  line-height: 1.35;
}

.step2-prompt-popover .step2-prompt-popover__input .v-input__slot {
  margin-bottom: 0;
}
</style>
